export type ConsentState = {
  version: string;
  updatedAt: string;
  expiresAt: string;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
};

export const CONSENT_VERSION = '1';
export const CONSENT_STORAGE_KEY = `sp_cookie_consent_v${CONSENT_VERSION}`;

// TTL 6 mesi (circa): 180 giorni
const TTL_DAYS = 180;

type LegacyConsent = {
  necessary?: boolean;
  functional?: boolean;
  accepted_at?: string;
};

function nowIso(): string {
  return new Date().toISOString();
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

function parseDateSafe(value: unknown): Date | null {
  if (!value || typeof value !== 'string') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function setTechnicalCookie(name: string, value: string, expiresAt: Date): void {
  try {
    const parts: string[] = [];
    parts.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
    parts.push(`Expires=${expiresAt.toUTCString()}`);
    parts.push('Path=/');
    parts.push('SameSite=Lax');
    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
      parts.push('Secure');
    }
    document.cookie = parts.join('; ');
  } catch {
    // ignore
  }
}

function deleteCookie(name: string): void {
  try {
    const base = `${encodeURIComponent(name)}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
    document.cookie = base;
    // Best-effort: tenta anche con domini comuni
    document.cookie = `${base}; Domain=sostegnopro.it`;
    document.cookie = `${base}; Domain=.sostegnopro.it`;
  } catch {
    // ignore
  }
}

export function hasValidConsent(consent: ConsentState | null | undefined): boolean {
  if (!consent) return false;
  if (consent.version !== CONSENT_VERSION) return false;
  if (consent.necessary !== true) return false;
  const updated = parseDateSafe(consent.updatedAt);
  const expires = parseDateSafe(consent.expiresAt);
  if (!updated || !expires) return false;
  if (expires.getTime() <= Date.now()) return false;
  return true;
}

function normalizeConsent(partial: Partial<ConsentState>): ConsentState {
  const now = new Date();
  const updatedAt = nowIso();
  const expiresAt = addDays(now, TTL_DAYS).toISOString();

  return {
    version: CONSENT_VERSION,
    updatedAt,
    expiresAt,
    necessary: true,
    analytics: Boolean(partial.analytics),
    marketing: Boolean(partial.marketing),
    functional: Boolean(partial.functional)
  };
}

function migrateLegacyConsent(): ConsentState | null {
  try {
    const raw = window.localStorage.getItem('cookie_consent');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LegacyConsent;

    // Legacy aveva solo necessary + functional. Non abbiamo segnali per analytics/marketing: default false.
    const acceptedAt = parseDateSafe(parsed?.accepted_at)?.toISOString();
    const now = new Date();
    const expires = addDays(now, TTL_DAYS);

    const migrated: ConsentState = {
      version: CONSENT_VERSION,
      updatedAt: acceptedAt || nowIso(),
      expiresAt: expires.toISOString(),
      necessary: true,
      analytics: false,
      marketing: false,
      functional: Boolean(parsed?.functional)
    };

    // Salva nel nuovo formato e rimuovi il legacy.
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(migrated));
    window.localStorage.removeItem('cookie_consent');
    setTechnicalCookie(CONSENT_STORAGE_KEY, '1', expires);

    return migrated;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentState | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return migrateLegacyConsent();
    }
    const parsed = JSON.parse(raw) as ConsentState;
    return hasValidConsent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setConsent(next: Partial<ConsentState>): ConsentState {
  const normalized = normalizeConsent(next);
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(normalized));
    const expires = parseDateSafe(normalized.expiresAt) || addDays(new Date(), TTL_DAYS);
    setTechnicalCookie(CONSENT_STORAGE_KEY, '1', expires);
  } catch {
    // ignore
  }
  return normalized;
}

export function resetConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.localStorage.removeItem('cookie_consent');
  } catch {
    // ignore
  }
  deleteCookie(CONSENT_STORAGE_KEY);
}
